import { Grid, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import BaseButton from 'src/common/components/base-button';
import SelectableTable, { IRowData } from 'src/common/components/selectable-table/selectable-table';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { LOCAL_ISO_CURRENCY_CODE } from 'src/common/helpers/localized-description-helper';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { ITenderGroup } from 'src/common/interfaces/dtos/ITenderGroup';
import { TenderCount } from 'src/common/interfaces/dtos/TenderCount';
import { IButton } from 'src/common/interfaces/IButton';
import Keypad from 'src/keypad/components/keypad';
import { Pagination } from 'src/pagination/pagination';
import { styles } from './styles';

const TABLE_SIZE = 8;

const NonDenominationalTenderCount: React.FC<NonDenominationlTenderProps> = ({
  titleText,
  subtitleText,
  tenderGroup,
  selectedTender,
  onTotalUpdated,
  leftHanded,
}): JSX.Element => {
  const classes = styles({ leftHanded });

  const isoSymbol =
    selectedTender && selectedTender.currency
      ? selectedTender.currency.isoSymbol
      : LOCAL_ISO_CURRENCY_CODE;
  const [tenderCountList, setTenderCountList] = useState<ITender[]>(
    tenderGroup.tenderCountList ?? [],
  );
  const [tenderSubtotalAmount, setTenderSubtotalAmount] = useState<number>(0);
  const [isItemSelected, setItemIsSelected] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(0);

  useEffect((): void => {
    const tenderList = tenderGroup.tenderCountList || [];
    setTenderCountList(tenderList);
    let subtotalAmount = 0;
    tenderList.forEach(element => (subtotalAmount += element.amount ?? 0));
    setTenderSubtotalAmount(subtotalAmount);
  }, [tenderGroup.tenderCountList]);

  // TODO: Pagination is currently fairly ad hoc but every implementation
  // seems to have a slightly different variation or requirement.
  // Once we find all the possible levers to flip, move this to
  // a common component and hook with all those levers
  useEffect(() => {
    setCurrentPage(0);
    setLastPage(Math.ceil(tenderCountList.length / TABLE_SIZE));
  }, [tenderCountList.length]);

  const updateCurrentPage = useCallback((newPage: number) => {
    setCurrentPage(newPage - 1);
  }, []);

  const addTender = (tenderAmount: number): void => {
    if (tenderAmount > 0) {
      const amount = +tenderAmount;
      const id = (tenderCountList.length + 1).toString();
      const newTenderLine: TenderCount = {
        amount,
        id,
        tenderId: selectedTender.id,
        isActive: false,
      };
      tenderCountList.push(newTenderLine);
      setTenderCountList(tenderCountList);
      updateTenderSubtotal();
      onTotalUpdated(amount);
    }
  };

  const removeTenderItem = (itemsToRemove: string[]): void => {
    const currentSubTotal = tenderSubtotalAmount;
    itemsToRemove.forEach(index => {
      tenderCountList.splice(
        tenderCountList.findIndex(item => item.id === index),
        1,
      );
    });

    tenderCountList.forEach((tender, index) => {
      tender.id = (index + 1).toString();
    });
    setTenderCountList(tenderCountList);

    updateTenderSubtotal();
    let subtotalAmount = 0;
    tenderCountList.forEach(element => (subtotalAmount += element.amount ?? 0));
    const totalToSubtract = currentSubTotal - subtotalAmount;
    onTotalUpdated(-totalToSubtract);
  };

  const removeAllTenderItems = (): void => {
    let subtotalAmount = 0;
    tenderCountList.forEach(element => (subtotalAmount += element.amount ?? 0));
    setTenderSubtotalAmount(0);
    tenderCountList.splice(0, tenderCountList.length);
    setTenderCountList(tenderCountList);
    onTotalUpdated(-subtotalAmount);
  };

  const updateTenderSubtotal = (): void => {
    let subtotalAmount = 0;
    tenderCountList.forEach(element => (subtotalAmount += element.amount ?? 0));
    setTenderSubtotalAmount(subtotalAmount);
  };

  const deleteSelectedTenderLineItem = (): void => {
    const selectedItems: string[] = [];
    tenderCountList.forEach(tender => {
      if (tender.isActive) {
        if (tender.id) {
          selectedItems.push(tender.id);
        }
      }
    });

    removeTenderItem(selectedItems);
    clearAllSelectedItems();
  };

  const clearLastSelectedItem = (): void => {
    setItemIsSelected(false);
    const selectedTenderLine = tenderCountList.find((x): boolean => !!x.isActive);
    if (selectedTenderLine) {
      selectedTenderLine.isActive = false;
    }
  };

  const clearAllSelectedItems = (): void => {
    setItemIsSelected(false);
    tenderCountList.forEach(tender => {
      tender.isActive = false;
    });
  };

  const setSelectedItems = (selectedItemIds: string[]): void => {
    selectedItemIds.forEach(itemId => {
      const selectedTenderLine = tenderCountList.find((x): boolean => x.id === itemId);
      if (selectedTenderLine) {
        selectedTenderLine.isActive = true;
        setItemIsSelected(true);
      }
    });
  };

  const clickItem = (selectedItems: string[]): void => {
    clearLastSelectedItem();
    setSelectedItems(selectedItems);
  };

  const renderTableData = (): IRowData[] => {
    return tenderCountList.map(
      (item): IRowData => {
        const itemId = item.id ?? '';
        return {
          rootCell: itemId,
          subcellOne: '',
          subcellTwo: convertCurrency(item.amount ?? 0, isoSymbol),
          id: itemId,
        };
      },
    );
  };

  const keypadSubmitButton: IButton = {
    label: 'OK',
    onClick: addTender,
  };

  return (
    <Grid
      item
      xs={9}
      className={`${classes.background} ${classes.nonDenomTenderCountContainer} fullWidth fullHeight`}
    >
      <Grid
        container
        className={'fullHeight'}
        data-testid='non-denom-tender-count-container'
        direction={leftHanded ? 'row-reverse' : 'row'}
      >
        <Grid item container direction='column' className='fullHeight' xs={8}>
          <Grid container className={classes.background}>
            <Typography
              className={classes.instructionTypography}
              data-testid='tender-count-header-text-1'
            >
              {titleText} Summary
            </Typography>
          </Grid>
          <Grid container className={classes.background}>
            <Grid item xs={6}>
              <Typography
                className={classes.tenderListHeader}
                data-testid='tender-count-header-text-2'
              >
                #
              </Typography>
            </Grid>
            <Grid item xs={6} style={{ textAlign: 'end' }}>
              <Typography
                className={classes.tenderListHeader}
                data-testid='tender-count-header-text-3'
              >
                Total Amount
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            className={classes.tenderListContainer}
            data-testid='non-denom-tender-count-list'
          >
            <SelectableTable
              tableData={renderTableData()}
              onSelection={clickItem}
              rowsPerPage={TABLE_SIZE}
              currentPage={currentPage}
              emptySelectionList={!isItemSelected}
            />
          </Grid>
          <Grid container className={classes.background}>
            <Grid item xs={9}>
              <Typography
                className={classes.subTotalLabelTypography}
                data-testid='tender-count-footer-text-1'
              >
                Total {subtitleText}
              </Typography>
            </Grid>
            <Grid item xs={3} style={{ textAlign: 'end' }}>
              <Typography
                className={classes.subTotalTypography}
                data-testid='tender-count-footer-text-2'
              >
                {convertCurrency(
                  tenderSubtotalAmount,
                  selectedTender && selectedTender.currency && selectedTender.currency.isoSymbol,
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            className={`${classes.background} ${classes.tenderLineItemActionContainer}`}
          >
            <Grid item xs={5} className={classes.paginationContainer}>
              <Grid container style={{ justifyContent: 'flex-start' }}>
                <Pagination
                  currentPage={lastPage ? currentPage + 1 : 0}
                  totalPages={lastPage}
                  updateCurrentPageHandler={updateCurrentPage}
                  testIdPrefix={'non-denominational-tender'}
                />
              </Grid>
            </Grid>
            <Grid item xs={7} className={classes.tenderListButtonContainer}>
              <BaseButton
                className={classes.okButton}
                onClick={removeAllTenderItems}
                disabled={tenderCountList.length === 0}
                data-testid={'delete-all-line-items-button'}
              >
                DELETE ALL
              </BaseButton>
              <BaseButton
                className={classes.okButton}
                onClick={deleteSelectedTenderLineItem}
                disabled={!isItemSelected}
                data-testid={'delete-line-item-button'}
              >
                DELETE
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} className={classes.keypadContainer}>
          <Grid
            container
            direction='column'
            className={`${classes.background} ${classes.keypad} fullHeight`}
            data-testid='non-denominational-tender-count-container'
          >
            <Keypad
              visible
              keypadTestIdPrefix={'non-denominational-amount-'}
              topLabelText={'Enter Amount'}
              submitButton={keypadSubmitButton}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface NonDenominationlTenderProps {
  titleText?: string;
  subtitleText?: string;
  tenderGroup: ITenderGroup;
  selectedTender: ITender;
  onTotalUpdated: (nonDenominationlTenderTotal: number) => void;
  leftHanded?: boolean;
}

export default NonDenominationalTenderCount;
